import React from 'react';
const { Icon, Card, Button, BarMini } = window;
/* ============================================================
   BOOKER — Tela: Índices econômicos (acompanhamento da API)
   Mostra os índices de reajuste alimentados pela API do Banco
   Central (SGS): valor acumulado 12m, histórico mensal e origem.
   ============================================================ */
var { useState, useEffect } = React;

const ORIGEM_INFO = {
  api: { label: 'Dados ao vivo · Banco Central', cor: 'var(--ok)', icon: 'check' },
  cache: { label: 'Cache local (última sincronização)', cor: 'var(--booker-orange)', icon: 'clock' },
  parcial: { label: 'Sincronização parcial', cor: 'var(--warn)', icon: 'alert' },
  fallback: { label: 'Sem conexão · valores de referência', cor: 'var(--danger)', icon: 'alert' },
  cache_geral: { label: 'Cache local', cor: 'var(--booker-orange)', icon: 'clock' },
  pendente: { label: 'Sincronizando…', cor: 'var(--text-muted)', icon: 'refresh' },
};

function Indices() {
  const DB = window.DB;
  const { INDICES, fmtNum } = DB;
  const [tick, setTick] = useState(0);
  const [carregando, setCarregando] = useState(false);

  // dispara/garante a busca ao abrir a aba
  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    DB.atualizarIndices(false, Date.now()).finally(() => { if (vivo) { setCarregando(false); setTick(t => t + 1); } });
    return () => { vivo = false; };
  }, []);

  const atualizar = () => {
    setCarregando(true);
    DB.atualizarIndices(true, Date.now()).finally(() => { setCarregando(false); setTick(t => t + 1); });
  };

  const meta = DB.indicesMeta || { origem: 'pendente', ts: 0 };
  const oi = ORIGEM_INFO[meta.origem] || ORIGEM_INFO.pendente;
  const quando = meta.ts ? new Date(meta.ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const chaves = Object.keys(DB.SERIES_INDICES || {});

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Índices econômicos</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
            Índices de reajuste consumidos da API pública do Banco Central (SGS) · usados nos cálculos de correção
          </p>
        </div>
        <Button variant="default" size="md" icon="refresh" onClick={atualizar}>
          {carregando ? 'Atualizando…' : 'Atualizar agora'}
        </Button>
      </div>

      {/* Barra de status da fonte */}
      <Card pad={16} style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ width: 40, height: 40, borderRadius: 11, background: oi.cor + '1e', color: oi.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={oi.icon} size={20} />
        </span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>{oi.label}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Última sincronização: {quando}</div>
        </div>
        <a href="https://www3.bcb.gov.br/sgspub/" target="_blank" rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--booker-orange)', textDecoration: 'none' }}>
          <Icon name="external" size={15} /> Portal SGS · Banco Central
        </a>
      </Card>

      {/* Cards dos índices */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(chaves.length, 2)}, 1fr)`, gap: 16, marginBottom: 18 }}>
        {chaves.map(key => <IndiceCard key={key + tick} idx={INDICES[key]} fmtNum={fmtNum} />)}
      </div>

      {/* Explicação da fonte */}
      <Card pad={20}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Icon name="brain" size={20} style={{ color: 'var(--booker-orange)' }} />
          <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>De onde vêm estes números</h3>
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text)', fontSize: 13.5, lineHeight: 1.7 }}>
          <li>Fonte: <b>API de Séries Temporais do Banco Central (SGS)</b> — pública, gratuita e sem chave de acesso.</li>
          <li>O <b>acumulado de 12 meses</b> é calculado a partir das variações mensais oficiais de cada índice.</li>
          <li>Os índices são divulgados <b>uma vez por mês</b>; o sistema revalida automaticamente a cada 12 horas e guarda um <b>cache local</b> para funcionar offline.</li>
          <li>Sem conexão, o sistema usa os <b>valores de referência</b> embutidos — nada deixa de funcionar.</li>
          <li>Séries consumidas: <b>IGP-M</b> (FGV · SGS 189) e <b>IPCA</b> (IBGE · SGS 433).</li>
        </ul>
      </Card>
    </div>
  );
}

function IndiceCard({ idx, fmtNum }) {
  const origem = idx.origem || 'fallback';
  const oi = ORIGEM_INFO[origem] || ORIGEM_INFO.fallback;
  const pontos = idx.historico || [];
  const valores = pontos.map(p => parseFloat(String(p.valor).replace(',', '.')));
  const labels = pontos.map(p => {
    const [, m] = p.data.split('/');
    return ['', 'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][+m] || m;
  });
  const ultimaVar = valores.length ? valores[valores.length - 1] : null;
  const subiu = idx.acum12 >= 0;

  return (
    <Card pad={20} hover>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: idx.color }} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.01em' }}>{idx.label}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{idx.fonteDado || idx.fonte}</div>
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 99, background: oi.cor + '1c', color: oi.cor, fontSize: 11, fontWeight: 800 }}>
          <span style={{ width: 6, height: 6, borderRadius: 99, background: 'currentColor' }} /> {origem.toUpperCase()}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span className="tnum" style={{ fontSize: 34, fontWeight: 800, color: idx.color, letterSpacing: '-.02em', lineHeight: 1 }}>
          {fmtNum(idx.acum12, 2)}%
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>acum. 12 meses</span>
      </div>
      {idx.dataRef && <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>referência: {idx.dataRef}</div>}

      {valores.length > 0 ? (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>Variação mensal (12m)</span>
            {ultimaVar != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 700, color: ultimaVar < 0 ? 'var(--danger)' : 'var(--ok)' }}>
                <Icon name={ultimaVar < 0 ? 'arrowDown' : 'arrowUp'} size={13} /> {fmtNum(ultimaVar, 2)}% no mês
              </span>
            )}
          </div>
          <BarMini data={valores} labels={labels} color={idx.color} height={64} />
        </div>
      ) : (
        <div style={{ marginTop: 18, padding: 14, background: 'var(--surface-inset)', borderRadius: 'var(--r)', fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 9, alignItems: 'center' }}>
          <Icon name="alert" size={16} style={{ color: 'var(--warn)' }} /> Histórico mensal indisponível offline — exibindo valor de referência.
        </div>
      )}
    </Card>
  );
}

Object.assign(window, { Indices });
